import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { cloService } from '../../services/cloService';
import AddCLOModal from './AddCLOModal';
import EditCLOModal from './EditCLOModal';

const { FiPlus, FiSearch, FiEdit, FiTrash2, FiEye, FiLock, FiTarget, FiBookOpen, FiFilter } = FiIcons;

const CLOManagement = () => {
  const { hasPermission, currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [clos, setClos] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCLO, setSelectedCLO] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourseCode, setFilterCourseCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const canManageCLOs = hasPermission('canManageQuestions'); // Same permission as questions

  // Load CLOs on component mount
  useEffect(() => {
    loadCLOs();
  }, [currentWorkspace?.id]);

  const loadCLOs = async () => {
    setIsLoading(true);
    try {
      if (currentWorkspace?.id) {
        const data = await cloService.getCLOs(currentWorkspace.id);
        setClos(data);
      } else {
        // Fallback to localStorage if no workspace
        const localCLOs = storage.getCLOs();
        setClos(localCLOs);
      }
    } catch (error) {
      console.error('Error loading CLOs:', error);
      // Fallback to localStorage
      const localCLOs = storage.getCLOs();
      setClos(localCLOs);
    } finally {
      setIsLoading(false);
    }
  };

  const courseCodes = [...new Set(clos.map(clo => clo.courseCode))];

  const filteredCLOs = clos.filter(clo => {
    const matchesSearch = 
      clo.courseCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clo.cloNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clo.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCourse = filterCourseCode === '' || clo.courseCode === filterCourseCode;
    
    return matchesSearch && matchesCourse;
  });

  const handleAddCLO = async (cloData) => {
    try {
      if (currentWorkspace?.id && user?.id) {
        await cloService.addCLO(cloData, currentWorkspace.id, user.id);
      } else {
        storage.addCLO(cloData);
      }
      await loadCLOs();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding CLO:', error);
      alert('Error adding CLO. Please try again.');
    }
  };

  const handleEditCLO = async (cloData) => {
    try {
      if (currentWorkspace?.id && selectedCLO?.id) {
        await cloService.updateCLO(selectedCLO.id, cloData, currentWorkspace.id);
      } else {
        storage.updateCLO(selectedCLO.id, cloData);
      }
      await loadCLOs();
      setShowEditModal(false);
      setSelectedCLO(null);
    } catch (error) {
      console.error('Error updating CLO:', error);
      alert('Error updating CLO. Please try again.');
    }
  };

  const handleDeleteCLO = async (id) => {
    if (window.confirm('Are you sure you want to delete this CLO? This action cannot be undone.')) {
      try {
        if (currentWorkspace?.id) {
          await cloService.deleteCLO(id, currentWorkspace.id);
        } else {
          storage.deleteCLO(id);
        }
        await loadCLOs();
      } catch (error) {
        console.error('Error deleting CLO:', error);
        alert('Error deleting CLO. Please try again.');
      }
    }
  };

  const openEditModal = (clo) => {
    setSelectedCLO(clo);
    setShowEditModal(true);
  };

  // Group CLOs by course code
  const groupedCLOs = filteredCLOs.reduce((groups, clo) => {
    const courseCode = clo.courseCode;
    if (!groups[courseCode]) {
      groups[courseCode] = [];
    }
    groups[courseCode].push(clo);
    return groups;
  }, {});

  if (isLoading) {
    return (
      <div className="space-y-6 p-8 ml-72">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Course Learning Outcomes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 ml-72">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Course Learning Outcomes</h2>
          <p className="text-gray-600 mt-1">
            {canManageCLOs 
              ? 'Manage Course Learning Outcomes (CLOs) for your courses'
              : 'Browse Course Learning Outcomes'
            }
          </p>
        </div>
        {canManageCLOs && (
          <motion.button
            onClick={() => setShowAddModal(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary-700 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiPlus} />
            <span>Add CLO</span>
          </motion.button>
        )}
      </div>

      {/* Statistics */}
      {clos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiBookOpen} className="text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-800">Total Courses</h3>
            </div>
            <p className="text-3xl font-bold text-primary-600">{courseCodes.length}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiTarget} className="text-green-600" />
              <h3 className="text-lg font-semibold text-gray-800">Total CLOs</h3>
            </div>
            <p className="text-3xl font-bold text-green-600">{clos.length}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiTarget} className="text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-800">Avg CLOs per Course</h3>
            </div>
            <p className="text-3xl font-bold text-purple-600">
              {courseCodes.length > 0 ? Math.round((clos.length / courseCodes.length) * 10) / 10 : 0}
            </p>
          </div>
        </div>
      )}

      {/* Permission Notice */}
      {!canManageCLOs && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiLock} className="text-blue-600" />
            <p className="text-blue-800 text-sm">
              You have view-only access to Course Learning Outcomes. Contact an admin to add or edit CLOs.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search CLOs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filterCourseCode}
            onChange={(e) => setFilterCourseCode(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Courses</option>
            {courseCodes.map(courseCode => (
              <option key={courseCode} value={courseCode}>{courseCode}</option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setFilterCourseCode('');
            }}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <SafeIcon icon={FiFilter} className="mr-2" />
            Clear
          </button>
        </div>
      </div>

      {/* CLOs List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">
            Course Learning Outcomes ({filteredCLOs.length})
          </h3>
        </div>

        {Object.keys(groupedCLOs).length === 0 ? (
          <div className="p-12 text-center">
            <SafeIcon icon={FiTarget} className="mx-auto text-4xl text-gray-300 mb-4" />
            <p className="text-gray-500">No Course Learning Outcomes found</p>
            <p className="text-sm text-gray-400 mt-1">
              {canManageCLOs ? 'Create your first CLO to get started' : 'No CLOs have been created yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {Object.entries(groupedCLOs).map(([courseCode, courseCLOs]) => (
              <div key={courseCode} className="p-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiBookOpen} className="mr-2 text-primary-600" />
                  {courseCode}
                  <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                    {courseCLOs.length} CLO{courseCLOs.length !== 1 ? 's' : ''}
                  </span>
                </h4>
                
                <div className="space-y-3">
                  <AnimatePresence>
                    {courseCLOs.map((clo, index) => (
                      <motion.div
                        key={clo.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                              {clo.cloNumber}
                            </span>
                            <span className="text-xs text-gray-500">
                              Created {new Date(clo.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-800 font-medium">{clo.description}</p>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          {canManageCLOs ? (
                            <>
                              <button
                                onClick={() => openEditModal(clo)}
                                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              >
                                <SafeIcon icon={FiEdit} />
                              </button>
                              <button
                                onClick={() => handleDeleteCLO(clo.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <SafeIcon icon={FiTrash2} />
                              </button>
                            </>
                          ) : (
                            <button className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                              <SafeIcon icon={FiEye} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals - Only show if user has permissions */}
      {canManageCLOs && (
        <>
          <AddCLOModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddCLO}
          />
          <EditCLOModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSubmit={handleEditCLO}
            clo={selectedCLO}
          />
        </>
      )}
    </div>
  );
};

export default CLOManagement;